import { LeftOutlined } from '@ant-design/icons'
import { AutoComplete, Button, Input, InputNumber, message } from 'antd'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import FoodSafetyClockField from 'src/components/food-safety/FoodSafetyClockField'
import FoodSafetyCorrectiveActionPanel from 'src/components/food-safety/FoodSafetyCorrectiveActionPanel'
import FoodSafetyDayNav from 'src/components/food-safety/FoodSafetyDayNav'
import {
  FoodSafetyExceptionFollowUp,
  FoodSafetyExceptionStatus,
} from 'src/components/food-safety/FoodSafetyExceptionFollowUp'
import FoodSafetyRecentDeliveries from 'src/components/food-safety/FoodSafetyRecentDeliveries'
import FoodSafetyUnresolvedStrip from 'src/components/food-safety/FoodSafetyUnresolvedStrip'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySubmitErrorBox,
  FoodSafetySubmitErrorLead,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FadeIn from 'src/components/styled/FadeIn'
import { ollieFabPageClearanceCss } from 'src/components/ollie/ollieFabPlacement'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_REPORTS_PAYWALL_REASON,
  FOOD_SAFETY_DELIVERY_REJECTION_OPTIONS,
  FOOD_SAFETY_DELIVERY_REJECTION_SCOPE_LABELS,
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_DELIVERY_TEMP_PRESETS,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SECTION_LABELS,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import { getVenueSuppliers } from 'src/features/VenueSuppliersSlice'
import useFoodSafetyExceptionFollowUps from 'src/hooks/useFoodSafetyExceptionFollowUps'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import useFoodSafetySubmissionKeys from 'src/hooks/useFoodSafetySubmissionKeys'
import useFoodSafetyRecipes from 'src/hooks/useFoodSafetyRecipes'
import useIsBlocked, { useCanAccess } from 'src/hooks/useIsBlocked'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import useFoodSafetyNewDayReload from 'src/hooks/useFoodSafetyNewDayReload'
import FoodSafetyCorrectReading from 'src/components/food-safety/FoodSafetyCorrectReading'
import FoodSafetyReportsPaywallModal from 'src/screens/food-safety/FoodSafetyReportsPaywallModal'
import {
  correctFoodSafetyLog,
  createFoodSafetyDefinition,
  createFoodSafetyLog,
  fetchFoodSafetyDay,
  finishFoodSafetyLog,
  recordFoodSafetyExceptionRecheck,
  undoFoodSafetySkipLog,
} from 'src/utils/foodSafetyApi'
import {
  FOOD_SAFETY_NEEDS_ACTION_PARAM,
  EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
  FOOD_SAFETY_CORRECTIVE_FIELD,
  focusFoodSafetyCorrectiveField,
  isFoodSafetyRecheckLog,
  pickFoodSafetyLogException,
  resolveFoodSafetyRecheckValue,
  validateFoodSafetyCorrectiveDraft,
} from 'src/utils/foodSafetyCorrectiveAction'
import {
  trackFoodSafetyLogSkipped,
  trackFoodSafetyLogSubmitted,
} from 'src/utils/foodSafetyAnalytics'
import {
  FOOD_SAFETY_SUBMIT_ERROR_LEAD,
  resolveFoodSafetySubmitAffordance,
  formatFoodSafetySubmitError,
} from 'src/utils/foodSafetyFeedback'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import {
  FOOD_SAFETY_DELIVERY_ACCEPT_LABEL,
  FOOD_SAFETY_DELIVERY_SAMPLING_GUIDANCE,
  FOOD_SAFETY_DELIVERY_REJECT_HINT,
  FOOD_SAFETY_DELIVERY_REJECT_LABEL,
  isAdHocDeliveryRejected,
  adHocDraftPatchClearTemplate,
  adHocDraftPatchForTemplate,
  rememberedFoodSafetyDraftPatch,
  adHocSupplierAutofillPatch,
  formatAdHocSupplierHint,
  filterFoodSafetyRememberedBySupplier,
  foodSafetyDeliveryFrequentBudget,
  adHocRangeHint,
  buildAdHocSkipBody,
  buildAdHocLogBody,
  cookingKindLabel,
  canUndoAdHocSkipLog,
  formatAdHocLogLine,
  formatAdHocStillNeededSkipCue,
  formatAdHocUndoSkipConfirmMessage,
  getAdHocSectionMeta,
  isAdHocNameValidationError,
  isAdHocNoRange,
  isAdHocTempOutOfRange,
  orderAdHocQuickPickTemplates,
  resolveAdHocDeliverySubmitNames,
  resolveAdHocNewReadingConfig,
  validateAdHocDraft,
  visibleAdHocQuickPickTemplates,
  FOOD_SAFETY_DELIVERY_EMPTY_LINE,
  FOOD_SAFETY_SAVE_FOODS_LINE,
  FOOD_SAFETY_SAVE_FOODS_LINK,
  FOOD_SAFETY_DELIVERY_LIST_PRODUCT_HINT,
  FOOD_SAFETY_STANDING_DELIVERY_HEADING,
  FOOD_SAFETY_SAVED_FOOD_HEADING,
  FOOD_SAFETY_COMPOSER_SELECT_SAVED,
  FOOD_SAFETY_COMPOSER_TYPE_NEW,
  FOOD_SAFETY_SAVE_AS_TEMPLATE,
  FOOD_SAFETY_SAVE_AS_TEMPLATE_DONE,
  buildFoodSafetyTemplateFromLog,
  canSaveFoodSafetyLogAsTemplate,
} from 'src/utils/foodSafetyAdHoc'
import {
  defaultDeliveryRejectionScope,
  deliveryBandTemp,
  isDeliveryDropOutOfRange,
  setDeliveryBandTemp,
  toggleDeliveryBand,
  venueSupplierNames,
} from 'src/utils/foodSafetyDeliveryDrop'
import {
  buildFoodSafetyBrowseWindow,
  formatFoodSafetyAdHocLogCountCue,
  formatFoodSafetyAdHocEmptyLogs,
  formatFoodSafetyAdHocLogsHeader,
  isFoodSafetyViewingToday,
  foodSafetyHomeHref,
  formatFoodSafetyOverviewBackLabel,
} from 'src/utils/foodSafetyHome'
import { foodSafetyTempToneFromOutOfRange } from 'src/utils/foodSafetyTempColour'
import {
  FOOD_SAFETY_COOLING_START_CHIPS,
  coolingStartTimeBefore,
  coolingTargetTempConfig,
  formatCoolingDuration,
  formatCoolingDurationLine,
  formatCoolingElapsedVsTarget,
  formatCoolingOpenBatchesLine,
  formatCoolingTimeHeadline,
  isCoolingTimeExceeded,
  listActiveCoolingBatches,
  resolveCoolingStartAt,
  resolveCoolingRemindAfterMinutes,
  resolveCoolingWindow,
} from 'src/utils/foodSafetyCooling'
import {
  FOOD_SAFETY_HELD_SINCE_CHIPS,
  foodSafetyHeldSinceBefore,
  FOOD_SAFETY_HOT_HOLDING_GUIDANCE,
  FOOD_SAFETY_HOT_HOLDING_CHECK_GUIDANCE,
  FOOD_SAFETY_HOLDING_NO_RANGE_LABEL,
  formatFoodSafetyHeldSinceLine,
  formatFoodSafetyHeldTimeHeadline,
  formatFoodSafetyHeldTooLong,
  holdingRangeTempConfig,
  isFoodSafetyHoldingSection,
  isFoodSafetyHoldingTimedSection,
  resolveFoodSafetyHeldSince,
} from 'src/utils/foodSafetyHoldingRange'
import {
  formatStillNeededTempStrip,
  listStillNeededFromDayItems,
} from 'src/utils/foodSafetyStillNeeded'
import FoodSafetyShoppingListSuggestBlock from 'src/components/food-safety/FoodSafetyShoppingListSuggestBlock'
import { isFoodSafetyRecipeSuggestSection } from 'src/utils/foodSafetyRecipeSuggest'
import {
  FOOD_SAFETY_SHOPPING_LIST_LOGGING_LIMIT,
  isFoodSafetyShoppingListSuggestSection,
  resolveFoodSafetyProductSupplier,
} from 'src/utils/foodSafetyShoppingListSuggest'
import { confirmWithFocusReturn } from 'src/utils/dialogFocus'
import { readFoodSafetyDeliveryCheckQuery } from 'src/utils/foodSafetyDeliveryCheckLink'
import { readFoodSafetyLinkedCoolingBatch } from 'src/utils/ollieActionDeepLinks'

const findAdHocNameInput = () =>
  document.querySelector('[data-testid="adhoc-name-input"]')

const emptyDraft = () => ({
  definitionName: '',
  value: null,
  finalTemp: null,
  // Cooling: HH:mm on the viewed day; blank finish = now (today).
  startTime: '',
  startTemp: null,
  finishTime: '',
  // Optional hot-holding start in the device's local clock time.
  heldSinceTime: '',
  heldSinceAt: null,
  accepted: true,
  supplierName: '',
  supplierAutofill: '',
  productName: '',
  deliveryBands: [{ band: 'chilled', value: null }],
  rejectionCode: '',
  rejectionScope: '',
  // Panel draft — chips/note/recheck, or the rejection reason (deliveries).
  action: { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT },
  actionNudged: false,
})

/**
 * Shared ad-hoc logging screen for Cooking, Cooling, and Deliveries.
 * Pattern: food name, optional shortcuts, cooling times, temperature, submit.
 */
const FoodSafetyAdHocSectionScreen = ({ section: sectionProp }) => {
  const blocked = useIsBlocked('food-safety')
  const canManage = useCanAccess('food-safety-manage')
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { venueId: routeVenueId, section: sectionParam } = useParams()
  const section = sectionProp || sectionParam
  const meta = getAdHocSectionMeta(section)

  const venue = useSelector(state => state.venue?.venue)
  const venueProducts = useSelector(
    state => state.venueProducts?.products || [],
  )
  const venueSuppliers = useSelector(
    state => state.venueSuppliers?.venueSuppliers || [],
  )
  const venueId = venue?._id || routeVenueId
  const recipes = useFoodSafetyRecipes(
    venueId,
    section === FOOD_SAFETY_SECTION.COOLING,
  )

  const queryDate = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('date') || undefined
  }, [location.search])
  const jumpToNeedsAction = useMemo(
    () =>
      new URLSearchParams(location.search).get(
        FOOD_SAFETY_NEEDS_ACTION_PARAM,
      ) === '1',
    [location.search],
  )
  const linkedReceipt = useMemo(
    () =>
      section === FOOD_SAFETY_SECTION.DELIVERIES
        ? readFoodSafetyDeliveryCheckQuery(location.search)
        : null,
    [location.search, section],
  )

  const [day, setDay] = useState(null)
  const newDayKey = useFoodSafetyNewDayReload(day)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState(() => emptyDraft())
  const [activeTemplate, setActiveTemplate] = useState(null)
  const [nameEntryMode, setNameEntryMode] = useState('saved')
  const [savingTemplateId, setSavingTemplateId] = useState(null)
  const [notRequiredNote, setNotRequiredNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [nameError, setNameError] = useState(null)
  const [coolingError, setCoolingError] = useState(null)
  const [coolingComposerMode, setCoolingComposerMode] = useState('start')
  const [finishingId, setFinishingId] = useState(null)
  const [finishDraft, setFinishDraft] = useState({
    finalTemp: null,
    finishTime: '',
  })
  const [heldError, setHeldError] = useState(null)
  const [holdingNow, setHoldingNow] = useState(() => moment())
  const [quickPicksExpanded, setQuickPicksExpanded] = useState(false)
  const [historyPaywallOpen, setHistoryPaywallOpen] = useState(false)
  const [lockAccess, setLockAccess] = useState(null)
  const latestDay = useFoodSafetyLatestRequest()
  const saveLock = useFoodSafetySaveLock()
  const submissionKeys = useFoodSafetySubmissionKeys()

  const patchDraft = patch => {
    if (
      Object.prototype.hasOwnProperty.call(patch, 'definitionName') ||
      Object.prototype.hasOwnProperty.call(patch, 'productName')
    ) {
      setNameError(null)
    }
    if (
      ['startTime', 'finishTime'].some(key =>
        Object.prototype.hasOwnProperty.call(patch, key),
      )
    ) {
      setCoolingError(null)
    }
    if (
      ['heldSinceTime', 'heldSinceAt'].some(key =>
        Object.prototype.hasOwnProperty.call(patch, key),
      )
    ) {
      setHeldError(null)
    }
    setSubmitError(null)
    setDraft(prev => ({
      ...prev,
      ...patch,
      ...(Object.prototype.hasOwnProperty.call(patch, 'heldSinceTime') &&
      !Object.prototype.hasOwnProperty.call(patch, 'heldSinceAt')
        ? { heldSinceAt: null }
        : {}),
    }))
  }

  const patchAction = patch => {
    setSubmitError(null)
    setActionError(null)
    setDraft(prev => ({ ...prev, action: { ...prev.action, ...patch } }))
  }

  const loadDay = useCallback(async () => {
    if (!venueId || !meta) return
    const isCurrent = latestDay.begin()
    setLoading(true)
    try {
      const data = await fetchFoodSafetyDay(venueId, {
        section,
        date: queryDate,
      })
      if (!isCurrent()) return
      setDay(data)
    } catch (error) {
      if (!isCurrent()) return
      if (error?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
        setLockAccess(error.historyAccess || null)
        setHistoryPaywallOpen(true)
        message.warning(error.message)
        // Stay on this section so the paywall remains visible (29.16).
        const earliest = error.historyAccess?.window?.earliestLocalDate
        if (earliest && earliest !== queryDate) {
          history.replace(
            `/v/${venueId}/food-safety/section/${section}?date=${earliest}`,
          )
        }
        return
      }
      console.error(error)
      message.error(error?.message || 'Could not load this section.')
    } finally {
      isCurrent.done()
      if (isCurrent()) setLoading(false)
    }
  }, [venueId, section, queryDate, history, meta, latestDay])

  /** After a save: throws on failure so it can't read as "Couldn't save". */
  /** Reload in place; resolves to the new day, or null when a switch won. */
  const refreshDay = useCallback(async () => {
    if (!venueId || !day?.localDate) return null
    const isCurrent = latestDay.beginQuiet()
    if (isCurrent.skipped) return null
    const data = await fetchFoodSafetyDay(venueId, {
      section,
      date: day.localDate,
    })
    if (!isCurrent()) return null
    setDay(data)
    return data
  }, [venueId, section, day?.localDate, latestDay])

  const refreshAfterSave = async () => {
    const refreshed = await refreshAfterFoodSafetySave(refreshDay)
    if (!refreshed) message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
    return refreshed
  }

  const followUps = useFoodSafetyExceptionFollowUps({
    venueId,
    onSaved: refreshDay,
  })
  const resetFollowUps = followUps.reset

  useEffect(() => {
    setDraft(emptyDraft())
    setActiveTemplate(null)
    setNameError(null)
    setSubmitError(null)
    setActionError(null)
    resetFollowUps()
    loadDay()
  }, [loadDay, section, newDayKey, resetFollowUps])

  useEffect(() => {
    if (!venueId || !isFoodSafetyShoppingListSuggestSection(section)) {
      return undefined
    }
    dispatch(fetchVenueProducts(venueId))
    return undefined
  }, [venueId, section, dispatch])

  useEffect(() => {
    if (!venueId || section !== FOOD_SAFETY_SECTION.DELIVERIES) return undefined
    dispatch(getVenueSuppliers(venueId))
    return undefined
  }, [venueId, section, dispatch])

  const browseWindow = useMemo(
    () =>
      buildFoodSafetyBrowseWindow(
        day?.historyAccess || lockAccess,
        day?.localDate,
      ),
    [day?.historyAccess, day?.localDate, lockAccess],
  )

  const goToSectionDate = nextDate => {
    if (!venueId || !section || !nextDate) return
    history.push(
      `/v/${venueId}/food-safety/section/${section}?date=${nextDate}`,
    )
  }

  const openHistoryPaywall = () => setHistoryPaywallOpen(true)

  const fillSupplierFor = productName => {
    if (section !== FOOD_SAFETY_SECTION.DELIVERIES) return
    const known = productName
      ? resolveFoodSafetyProductSupplier(venueProducts, productName).name
      : ''
    setDraft(prev => ({ ...prev, ...adHocSupplierAutofillPatch(prev, known) }))
  }

  const onFoodNameChange = value => {
    setNameEntryMode('new')
    if (
      activeTemplate &&
      value.trim().toLowerCase() !==
        String(activeTemplate.name || '')
          .trim()
          .toLowerCase()
    ) {
      setActiveTemplate(null)
    }
    if (section === FOOD_SAFETY_SECTION.DELIVERIES) {
      patchDraft({ definitionName: value, productName: value })
    } else {
      patchDraft({ definitionName: value })
    }
  }

  const selectRemembered = memory => {
    setNameEntryMode('new')
    setActiveTemplate(null)
    setNameError(null)
    patchDraft(rememberedFoodSafetyDraftPatch(section, memory, draft))
    requestAnimationFrame(() => {
      const root = document.querySelector('[data-testid="adhoc-temp-input"]')
      const input = root?.querySelector?.('input') || root
      input?.focus?.()
    })
  }

  const selectTemplate = def => {
    setNameEntryMode('saved')
    setActiveTemplate(def)
    setNotRequiredNote('')
    setNameError(null)
    patchDraft(adHocDraftPatchForTemplate(section, def))
    fillSupplierFor(def?.name)
    requestAnimationFrame(() => {
      const root = document.querySelector('[data-testid="adhoc-temp-input"]')
      const input = root?.querySelector?.('input') || root
      input?.focus?.()
    })
  }

  const clearTemplate = () => {
    setActiveTemplate(null)
    setNotRequiredNote('')
    setNameError(null)
    patchDraft(adHocDraftPatchClearTemplate())
    fillSupplierFor('')
  }

  const selectShoppingListProduct = title => {
    const name = String(title || '').trim()
    if (!name) return
    const match = (day?.items || [])
      .map(i => i.definition)
      .filter(Boolean)
      .find(
        def =>
          String(def.name || '')
            .trim()
            .toLowerCase() === name.toLowerCase(),
      )
    if (match) {
      selectTemplate(match)
      return
    }
    setActiveTemplate(null)
    patchDraft({
      definitionName: name,
      productName:
        section === FOOD_SAFETY_SECTION.DELIVERIES ? name : draft.productName,
      action: { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT },
    })
    fillSupplierFor(name)
    requestAnimationFrame(() => {
      const root = document.querySelector('[data-testid="adhoc-temp-input"]')
      const input = root?.querySelector?.('input') || root
      input?.focus?.()
    })
  }

  const knownSupplierNames = useMemo(
    () => venueSupplierNames(venueSuppliers),
    [venueSuppliers],
  )
  const supplierHint =
    section === FOOD_SAFETY_SECTION.DELIVERIES
      ? formatAdHocSupplierHint(
          draft,
          resolveFoodSafetyProductSupplier(venueProducts, draft.productName)
            .options,
        ) ||
        (knownSupplierNames.length
          ? 'Pick a supplier you buy from, type one that isn’t listed, or leave it blank.'
          : '')
      : ''

  // Holding logs against the venue range from the day payload, never the item.
  const holdingConfig = isFoodSafetyHoldingSection(section)
    ? holdingRangeTempConfig(day?.holdingRange)
    : null
  const isCooling = section === FOOD_SAFETY_SECTION.COOLING
  const linkedCoolingLogId = useMemo(
    () =>
      isCooling ? readFoodSafetyLinkedCoolingBatch(location.search) : null,
    [isCooling, location.search],
  )
  const openedOllieFinishRef = useRef(null)
  useEffect(() => {
    if (!linkedCoolingLogId) return
    if (openedOllieFinishRef.current === linkedCoolingLogId) return
    const batches = listActiveCoolingBatches(day)
    if (!batches.some(batch => String(batch._id) === linkedCoolingLogId)) return
    openedOllieFinishRef.current = linkedCoolingLogId
    setFinishingId(linkedCoolingLogId)
    setFinishDraft({ finalTemp: null, finishTime: '' })
    requestAnimationFrame(() => {
      const root = document.querySelector(
        '[data-testid="adhoc-cooling-finish-temp"]',
      )
      const input = root?.querySelector?.('input') || root
      input?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
      input?.focus?.()
    })
  }, [linkedCoolingLogId, day])
  // Cooling: template range wins, else the venue target from the day payload.
  const coolingConfig = isCooling
    ? coolingTargetTempConfig(day?.coolingTarget, activeTemplate?.config)
    : null
  const config = resolveAdHocNewReadingConfig(
    section,
    holdingConfig || coolingConfig || activeTemplate?.config,
  )
  const noRange = isAdHocNoRange(section, config)
  const outOfRange =
    section === FOOD_SAFETY_SECTION.DELIVERIES
      ? isDeliveryDropOutOfRange(draft.deliveryBands)
      : isAdHocTempOutOfRange(section, draft, config)
  const rejected = isAdHocDeliveryRejected(section, draft)
  const coolingMaxMinutes = isCooling
    ? day?.coolingTarget?.maxMinutes ?? null
    : null
  const coolingViewingToday = isFoodSafetyViewingToday(
    day?.localDate,
    day?.todayLocalDate,
  )
  const coolingStartMode =
    isCooling && coolingViewingToday && coolingComposerMode !== 'oneshot'
  const coolingWindow =
    isCooling && !coolingStartMode
      ? resolveCoolingWindow({
          localDate: day?.localDate,
          viewingToday: coolingViewingToday,
          startTime: draft.startTime,
          finishTime: draft.finishTime,
        })
      : null
  const coolingTooLong =
    isCooling &&
    !coolingWindow?.error &&
    isCoolingTimeExceeded(coolingWindow?.minutes, coolingMaxMinutes)
  const holdingMaxMinutes = isFoodSafetyHoldingTimedSection(section)
    ? day?.holdingRange?.maxMinutes ?? null
    : null
  // Start-time tracking does not require imposing a maximum holding period.
  const heldSinceOffered =
    isFoodSafetyHoldingTimedSection(section) &&
    isFoodSafetyViewingToday(day?.localDate, day?.todayLocalDate)
  useEffect(() => {
    if (!heldSinceOffered || !draft.heldSinceTime) return undefined
    setHoldingNow(moment())
    const timer = setInterval(() => setHoldingNow(moment()), 60000)
    return () => clearInterval(timer)
  }, [heldSinceOffered, draft.heldSinceTime])
  const heldSince = heldSinceOffered
    ? resolveFoodSafetyHeldSince({
        localDate: holdingNow.format('YYYY-MM-DD'),
        heldSinceTime: draft.heldSinceTime,
        heldSinceAt: draft.heldSinceAt,
        now: holdingNow,
      })
    : null
  const heldTooLongText =
    heldSince && !heldSince.error
      ? formatFoodSafetyHeldTooLong(heldSince.minutes, holdingMaxMinutes)
      : null
  const needsAction =
    (outOfRange && !rejected) || coolingTooLong || Boolean(heldTooLongText)
  const actionChosen = rejected
    ? String(draft.action?.notes || '').trim()
    : draft.action?.type || ''
  const submitAffordance = resolveFoodSafetySubmitAffordance({
    outOfRange: needsAction,
    corrective: actionChosen,
    actionOptional: true,
    withActionLabel: rejected ? 'Save with reason' : undefined,
    saveFailed: Boolean(submitError),
  })
  const recheckOffered =
    outOfRange &&
    !rejected &&
    section !== FOOD_SAFETY_SECTION.DELIVERIES &&
    isFoodSafetyViewingToday(day?.localDate, day?.todayLocalDate)

  const focusFirstActionChip = () => {
    focusFoodSafetyCorrectiveField(
      'adhoc-corrective',
      rejected
        ? FOOD_SAFETY_CORRECTIVE_FIELD.NOTE
        : FOOD_SAFETY_CORRECTIVE_FIELD.ACTIONS,
    )
  }

  /** First Enter on an out-of-range temp shows the actions; the next saves. */
  const submitFromTemp = () => {
    if (saveLock.isAnyHeld()) return
    if (needsAction && !actionChosen && !draft.actionNudged) {
      patchDraft({ actionNudged: true })
      focusFirstActionChip()
      return
    }
    submit()
  }

  const submit = async () => {
    const viewingTodayNow = isFoodSafetyViewingToday(
      day?.localDate,
      day?.todayLocalDate,
    )
    const startCooling =
      isCooling && viewingTodayNow && coolingComposerMode === 'start'
    const submitStart = startCooling
      ? resolveCoolingStartAt({
          localDate: day?.localDate,
          viewingToday: viewingTodayNow,
          startTime: draft.startTime,
        })
      : null
    const submitWindow =
      isCooling && !startCooling
        ? resolveCoolingWindow({
            localDate: day?.localDate,
            viewingToday: viewingTodayNow,
            startTime: draft.startTime,
            finishTime: draft.finishTime,
          })
        : null
    const submitHeldSince = heldSinceOffered
      ? resolveFoodSafetyHeldSince({
          localDate: moment().format('YYYY-MM-DD'),
          heldSinceTime: draft.heldSinceTime,
          heldSinceAt: draft.heldSinceAt,
        })
      : null
    const validationError = validateAdHocDraft(section, draft, {
      hasTemplate: Boolean(activeTemplate),
      config: activeTemplate?.config,
      coolingMode: startCooling ? 'start' : 'oneshot',
    })
    if (validationError) {
      if (isAdHocNameValidationError(validationError)) {
        setNameError(validationError)
        requestAnimationFrame(() => {
          const nameInput = document.querySelector(
            '[data-testid="adhoc-name-input"]',
          )
          nameInput?.focus?.()
          nameInput?.scrollIntoView?.({
            block: 'nearest',
            behavior: 'smooth',
          })
        })
        return
      }
      setNameError(null)
      message.warning(validationError)
      return
    }
    setNameError(null)
    if (submitStart?.error || submitWindow?.error) {
      setCoolingError(submitStart?.error || submitWindow.error)
      requestAnimationFrame(() => {
        document
          .querySelector('[data-testid="adhoc-cooling-times"]')
          ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
      })
      return
    }
    if (submitHeldSince?.error) {
      setHeldError(submitHeldSince.error)
      return
    }
    const actionDraftError =
      needsAction && !rejected
        ? validateFoodSafetyCorrectiveDraft(draft.action)
        : null
    if (actionDraftError) {
      setActionError(actionDraftError)
      return
    }
    const recheckValue = recheckOffered
      ? resolveFoodSafetyRecheckValue(draft.action)
      : null

    if (!saveLock.tryAcquire()) return
    setSubmitting(true)
    try {
      const body = buildAdHocLogBody({
        section,
        localDate: day.localDate,
        draft:
          section === FOOD_SAFETY_SECTION.DELIVERIES
            ? {
                ...draft,
                ...resolveAdHocDeliverySubmitNames(draft, activeTemplate),
              }
            : isCooling && startCooling
            ? {
                ...draft,
                startAt: submitStart?.startAt,
                finalTemp: null,
                remindAfterMinutes: resolveCoolingRemindAfterMinutes(
                  day?.coolingTarget,
                ),
              }
            : isCooling
            ? {
                ...draft,
                startAt: submitWindow?.startAt || undefined,
                endAt: submitWindow?.endAt || undefined,
              }
            : submitHeldSince?.heldSinceAt
            ? { ...draft, heldSinceAt: submitHeldSince.heldSinceAt }
            : draft,
        templateDefinition: activeTemplate,
      })
      const saveSlot = `${day.localDate}:composer`
      let saved
      try {
        saved = await createFoodSafetyLog(venueId, body, {
          submissionKey: submissionKeys.keyFor(saveSlot, {
            draft,
            templateId: activeTemplate?._id || null,
          }),
        })
      } catch (error) {
        // 29.37 — keep composer draft; Retry on submit.
        const msg = formatFoodSafetySubmitError(error)
        setSubmitError(msg)
        message.error(msg)
        return
      }
      submissionKeys.clear(saveSlot)
      trackFoodSafetyLogSubmitted({
        venueId,
        section,
        skipped: false,
        fromTemplate: Boolean(activeTemplate?._id),
      })
      if (needsAction && !actionChosen) {
        message.warning(
          rejected
            ? 'Saved — add why it was rejected when you can'
            : 'Saved — needs corrective action',
        )
      } else {
        message.success(startCooling ? 'Cooling started' : 'Logged')
      }
      // A cooling log can raise a time and a temp exception — recheck the temp.
      const recheckTarget =
        (saved?.exceptions || []).find(e => e?.kind === 'out_of_range') ||
        saved?.exception
      if (recheckValue != null && recheckTarget?._id) {
        try {
          await recordFoodSafetyExceptionRecheck(venueId, recheckTarget._id, {
            value: recheckValue,
          })
        } catch (recheckError) {
          message.warning(
            `Reading saved, but the recheck didn’t save — ${formatFoodSafetySubmitError(
              recheckError,
            )}`,
          )
        }
      }
      setActionError(null)
      setCoolingError(null)
      setDraft(emptyDraft())
      setActiveTemplate(null)
      setNameError(null)
      setSubmitError(null)
      await refreshAfterSave()
      requestAnimationFrame(() => {
        const nameInput = document.querySelector(
          '[data-testid="adhoc-name-input"]',
        )
        nameInput?.focus?.()
      })
    } finally {
      saveLock.release()
      setSubmitting(false)
    }
  }

  const submitFinishBatch = async batch => {
    if (!venueId || !batch?._id) return
    if (finishDraft.finalTemp == null || finishDraft.finalTemp === '') {
      message.warning('Enter the finish temperature.')
      return
    }
    const viewingTodayNow = isFoodSafetyViewingToday(
      day?.localDate,
      day?.todayLocalDate,
    )
    const window = resolveCoolingWindow({
      localDate: day?.localDate,
      viewingToday: viewingTodayNow,
      startTime: moment(batch.payload?.startAt).format('HH:mm'),
      finishTime: finishDraft.finishTime,
    })
    if (window?.error) {
      setCoolingError(window.error)
      return
    }
    if (!saveLock.tryAcquire()) return
    setSubmitting(true)
    try {
      await finishFoodSafetyLog(venueId, String(batch._id), {
        payload: {
          finalTemp: Number(finishDraft.finalTemp),
          endAt: window?.endAt,
        },
      })
      message.success('Cooling finished')
      setFinishingId(null)
      setFinishDraft({ finalTemp: null, finishTime: '' })
      await refreshAfterSave()
    } catch (error) {
      message.error(formatFoodSafetySubmitError(error))
    } finally {
      saveLock.release()
      setSubmitting(false)
    }
  }

  const skipQuickPickToday = async definition => {
    if (!venueId || !day?.localDate || !definition?._id) return
    if (!saveLock.tryAcquire()) return
    setSubmitting(true)
    try {
      const body = buildAdHocSkipBody({
        definitionId: definition._id,
        localDate: day.localDate,
        note: notRequiredNote,
      })
      const skipSlot = `${day.localDate}:skip:${definition._id}`
      try {
        await createFoodSafetyLog(venueId, body, {
          submissionKey: submissionKeys.keyFor(skipSlot, null),
        })
      } catch (error) {
        message.error(
          error?.response?.data?.message ||
            error?.message ||
            'Could not mark this as not required.',
        )
        return
      }
      submissionKeys.clear(skipSlot)
      trackFoodSafetyLogSkipped({
        venueId,
        section,
        definitionId: String(definition._id),
      })
      message.success(`Marked ${definition.name} as not required today`)
      setNotRequiredNote('')
      if (String(activeTemplate?._id) === String(definition._id)) {
        setDraft(emptyDraft())
        setActiveTemplate(null)
      }
      setNameError(null)
      await refreshAfterSave()
    } finally {
      saveLock.release()
      setSubmitting(false)
    }
  }

  const saveCorrection = async (log, { value, note }) => {
    if (!venueId || !log?._id) return false
    if (!saveLock.tryAcquire()) return false
    setSubmitting(true)
    try {
      await correctFoodSafetyLog(venueId, String(log._id), { value, note })
      await refreshAfterSave()
      return true
    } catch (error) {
      message.error(formatFoodSafetySubmitError(error))
      return false
    } finally {
      saveLock.release()
      setSubmitting(false)
    }
  }

  const undoSkipLog = async log => {
    if (!venueId || !log?._id) return
    if (!saveLock.tryAcquire()) return
    setSubmitting(true)
    try {
      try {
        await undoFoodSafetySkipLog(venueId, String(log._id))
      } catch (error) {
        message.error(
          error?.response?.data?.message || error?.message || 'Could not undo.',
        )
        return
      }
      message.success(`${log.definitionName || 'This check'} is needed again`)
      await refreshAfterSave()
    } finally {
      saveLock.release()
      setSubmitting(false)
    }
  }

  const confirmUndoSkipLog = log => {
    if (!log?._id || submitting) return
    confirmWithFocusReturn(
      {
        title: 'Undo?',
        content: formatAdHocUndoSkipConfirmMessage(log.definitionName),
        okText: 'Undo',
        cancelText: 'Cancel',
        onOk: () => undoSkipLog(log),
      },
      { fallbackFocus: findAdHocNameInput },
    )
  }

  const saveLogAsTemplate = async log => {
    const defs = (day?.items || []).map(i => i.definition).filter(Boolean)
    if (
      !venueId ||
      !canManage ||
      !canSaveFoodSafetyLogAsTemplate(section, log, defs)
    ) {
      return
    }
    const body = buildFoodSafetyTemplateFromLog(section, log)
    if (!body) return
    setSavingTemplateId(String(log._id))
    try {
      await createFoodSafetyDefinition(venueId, body)
      message.success(FOOD_SAFETY_SAVE_AS_TEMPLATE_DONE)
      await refreshAfterSave()
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          'Could not save as a food',
      )
    } finally {
      setSavingTemplateId(null)
    }
  }

  if (blocked) return <PermissionBlockScreen />
  if (!meta) {
    return (
      <Wrapper>
        <BodyText>Unknown Food Safety section.</BodyText>
      </Wrapper>
    )
  }
  if (!venueId || (loading && !day)) return <Loading />

  const templates = (day?.items || []).map(i => i.definition).filter(Boolean)
  const rememberedAll = Array.isArray(day?.remembered) ? day.remembered : []
  const rememberedForSupplier = filterFoodSafetyRememberedBySupplier(
    rememberedAll,
    section === FOOD_SAFETY_SECTION.DELIVERIES ? draft.supplierName : '',
  )
  const deliveryFrequentBudget = foodSafetyDeliveryFrequentBudget({
    rememberedCount: rememberedForSupplier.length,
    supplierName:
      section === FOOD_SAFETY_SECTION.DELIVERIES ? draft.supplierName : '',
    listDefaultLimit: FOOD_SAFETY_SHOPPING_LIST_LOGGING_LIMIT,
  })
  const remembered = rememberedForSupplier.slice(
    0,
    deliveryFrequentBudget.rememberedLimit,
  )
  const logs = [
    ...(day?.items || []).flatMap(i => i.logs || []),
    ...(day?.orphanLogs || []),
  ]
    .filter(
      log => !isFoodSafetyRecheckLog(log) && log?.status !== 'in_progress',
    )
    .sort((a, b) => String(a.completedAt).localeCompare(String(b.completedAt)))

  const activeBatches = isCooling ? listActiveCoolingBatches(day) : []
  const stillNeededTemplates = listStillNeededFromDayItems({
    items: day?.items || [],
    orphanLogs: day?.orphanLogs || [],
  })
  const stillNeededIds = new Set(stillNeededTemplates.map(d => String(d._id)))
  const stillNeededStrip =
    formatCoolingOpenBatchesLine(
      activeBatches.map(batch => batch.productName || batch.definitionName),
      activeBatches.length,
    ) || formatStillNeededTempStrip(stillNeededTemplates)
  const orderedTemplates = orderAdHocQuickPickTemplates({
    templates,
    neededIds: stillNeededIds,
  })
  const {
    visible: visibleTemplates,
    hiddenCount: hiddenQuickPickCount,
    truncated: quickPicksTruncated,
  } = visibleAdHocQuickPickTemplates({
    templates: orderedTemplates,
    expanded: quickPicksExpanded,
    activeId: activeTemplate?._id,
  })
  const tempValue =
    section === FOOD_SAFETY_SECTION.COOLING ? draft.finalTemp : draft.value
  const draftTempTone =
    tempValue == null || tempValue === '' || noRange
      ? 'unknown'
      : foodSafetyTempToneFromOutOfRange(outOfRange)
  const rangeHint = noRange
    ? FOOD_SAFETY_HOLDING_NO_RANGE_LABEL
    : adHocRangeHint(section, config)
  const setTempValue = val => {
    if (section === FOOD_SAFETY_SECTION.COOLING) {
      patchDraft({ finalTemp: val, actionNudged: false })
    } else {
      patchDraft({ value: val, actionNudged: false })
    }
  }

  const overviewHref = foodSafetyHomeHref(venueId, {
    localDate: day?.localDate,
    todayLocalDate: day?.todayLocalDate,
  })
  const overviewLabel = formatFoodSafetyOverviewBackLabel(
    day?.localDate,
    day?.todayLocalDate,
  )
  const viewingToday = coolingViewingToday
  const logsHeader = formatFoodSafetyAdHocLogsHeader({ viewingToday })
  const recordedCount = logs.filter(
    log => log?.payload?.skipped !== true,
  ).length
  const logCountCue = formatFoodSafetyAdHocLogCountCue(recordedCount, {
    viewingToday,
  })
  const canMarkNotRequired =
    viewingToday &&
    !day?.closed &&
    activeTemplate &&
    (stillNeededIds.has(String(activeTemplate._id)) ||
      (activeTemplate.scheduleMode === 'scheduled' &&
        (day?.items || []).some(
          item =>
            String(item.definition?._id) === String(activeTemplate._id) &&
            item.expectation?.due &&
            !item.outOfService &&
            !item.logs?.length,
        )))

  return (
    <Wrapper>
      <TopBar>
        <BackLink to={overviewHref} data-testid='fs-section-back'>
          <LeftOutlined /> {overviewLabel}
        </BackLink>
      </TopBar>

      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>
              {FOOD_SAFETY_SECTION_LABELS[section]}
            </FoodSafetyPageTitle>
            <BodyText>
              {/* 29.58 — sticky strip owns still-needed; hero stays calm */}
              {logCountCue || meta.emptyHint || 'Log when it happens'}
            </BodyText>
          </div>
          {day?.localDate ? (
            <FoodSafetyDayNav
              localDate={day.localDate}
              browseWindow={browseWindow}
              onChangeDate={goToSectionDate}
              onBrowseLocked={openHistoryPaywall}
              disabled={loading}
            />
          ) : null}
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>

      {section === FOOD_SAFETY_SECTION.HOT_HOLDING ? (
        <FoodSafetySurfaceCard data-testid='hot-holding-guidance'>
          <BodyText>{FOOD_SAFETY_HOT_HOLDING_GUIDANCE}</BodyText>
          <BodyText>{FOOD_SAFETY_HOT_HOLDING_CHECK_GUIDANCE}</BodyText>
        </FoodSafetySurfaceCard>
      ) : null}

      {day?.localDate ? (
        <FoodSafetyUnresolvedStrip
          venueId={venueId}
          section={section}
          viewedLocalDate={day.localDate}
          todayLocalDate={day.todayLocalDate}
          refreshKey={day}
          autoJump={jumpToNeedsAction}
          testIdPrefix='adhoc-unresolved'
        />
      ) : null}

      {stillNeededStrip ? (
        <StillNeededSticky data-testid='food-safety-still-needed'>
          <StillNeededStickyLabel>{stillNeededStrip}</StillNeededStickyLabel>
          <StillNeededStickyHint>
            {formatAdHocStillNeededSkipCue()}
          </StillNeededStickyHint>
        </StillNeededSticky>
      ) : null}

      {section === FOOD_SAFETY_SECTION.DELIVERIES &&
      (viewingToday || templates.length > 0 || canManage) ? (
        <DeliverySources>
          {viewingToday && day?.localDate ? (
            <FoodSafetyRecentDeliveries
              venueId={venueId}
              localDate={day.localDate}
              embedded
              templates={templates}
              initialReceipt={linkedReceipt}
              onSaved={refreshDay}
            />
          ) : null}
          {templates.length > 0 || canManage ? (
            <StandingCard data-testid='delivery-standing'>
              <StandingTitle>
                {FOOD_SAFETY_STANDING_DELIVERY_HEADING}
              </StandingTitle>
              {templates.length > 0 ? (
                <>
                  <ChipRow>
                    {visibleTemplates.map(def => {
                      const active =
                        String(activeTemplate?._id) === String(def._id)
                      return (
                        <Chip
                          key={String(def._id)}
                          type='button'
                          data-active={active ? 'true' : 'false'}
                          data-testid={`delivery-food-${def._id}`}
                          onClick={() =>
                            active ? clearTemplate() : selectTemplate(def)
                          }
                        >
                          {def.name}
                        </Chip>
                      )
                    })}
                  </ChipRow>
                  {quickPicksTruncated || quickPicksExpanded ? (
                    <ShowMoreButton
                      type='button'
                      onClick={() => setQuickPicksExpanded(v => !v)}
                      data-testid='delivery-foods-more'
                    >
                      {quickPicksExpanded
                        ? 'Show fewer'
                        : `Show ${hiddenQuickPickCount} more`}
                    </ShowMoreButton>
                  ) : null}
                </>
              ) : (
                <>
                  <SaveFoodsLine>
                    {FOOD_SAFETY_DELIVERY_EMPTY_LINE}
                  </SaveFoodsLine>
                  <SetupLink
                    to={`/v/${venueId}/food-safety/setup?section=${section}`}
                    data-testid='delivery-standing-setup'
                  >
                    {FOOD_SAFETY_SAVE_FOODS_LINK}
                  </SetupLink>
                </>
              )}
            </StandingCard>
          ) : null}
        </DeliverySources>
      ) : null}

      <Composer data-testid='adhoc-composer' data-accent='lime'>
        <ComposerTitle>New log</ComposerTitle>
        {section === FOOD_SAFETY_SECTION.DELIVERIES ? (
          <>
            <BodyText>{FOOD_SAFETY_DELIVERY_SAMPLING_GUIDANCE}</BodyText>
            <Field>
              <FieldLabel as='label' htmlFor='adhoc-supplier-field'>
                Supplier (optional)
              </FieldLabel>
              <AutoComplete
                aria-label='Supplier (optional)'
                value={draft.supplierName}
                dataSource={knownSupplierNames}
                onChange={value => patchDraft({ supplierName: value })}
                filterOption={(input, option) =>
                  String(option.props.children || '')
                    .toLowerCase()
                    .includes(String(input || '').toLowerCase())
                }
                placeholder='e.g. Lynas'
              >
                <Input
                  id='adhoc-supplier-field'
                  aria-describedby={
                    supplierHint ? 'adhoc-supplier-hint' : undefined
                  }
                  data-testid='adhoc-supplier-input'
                />
              </AutoComplete>
              {supplierHint ? (
                <RangeHint
                  id='adhoc-supplier-hint'
                  data-testid='adhoc-supplier-hint'
                >
                  {supplierHint}
                </RangeHint>
              ) : null}
            </Field>
          </>
        ) : null}
        {section === FOOD_SAFETY_SECTION.DELIVERIES ? (
          <Field data-invalid={nameError ? 'true' : 'false'}>
            <FieldLabel as='label' htmlFor='adhoc-name-field'>
              Product
            </FieldLabel>
            <Input
              id='adhoc-name-field'
              data-testid='adhoc-name-input'
              value={draft.definitionName}
              onChange={e => onFoodNameChange(e.target.value)}
              placeholder={meta.namePlaceholder}
              aria-invalid={nameError ? 'true' : 'false'}
              aria-describedby={nameError ? 'adhoc-name-error' : undefined}
            />
            {nameError ? (
              <FieldError
                id='adhoc-name-error'
                data-testid='adhoc-name-error'
                role='alert'
              >
                {nameError}
              </FieldError>
            ) : null}
          </Field>
        ) : (
          <Field data-invalid={nameError ? 'true' : 'false'}>
            <FieldLabel>Food</FieldLabel>
            <ChipRow
              role='group'
              aria-label='Food name'
              data-testid='adhoc-name-mode'
            >
              <Chip
                type='button'
                data-active={nameEntryMode === 'saved' ? 'true' : 'false'}
                data-needed='false'
                data-testid='adhoc-name-mode-saved'
                onClick={() => setNameEntryMode('saved')}
              >
                {FOOD_SAFETY_COMPOSER_SELECT_SAVED}
              </Chip>
              <Chip
                type='button'
                data-active={nameEntryMode === 'new' ? 'true' : 'false'}
                data-needed='false'
                data-testid='adhoc-name-mode-new'
                onClick={() => {
                  setNameEntryMode('new')
                  if (activeTemplate) clearTemplate()
                  requestAnimationFrame(() => findAdHocNameInput()?.focus?.())
                }}
              >
                {FOOD_SAFETY_COMPOSER_TYPE_NEW}
              </Chip>
            </ChipRow>
            <Input
              id='adhoc-name-field'
              data-testid='adhoc-name-input'
              value={draft.definitionName}
              onChange={e => onFoodNameChange(e.target.value)}
              placeholder={meta.namePlaceholder}
              aria-label='Food'
              aria-hidden={nameEntryMode === 'new' ? undefined : true}
              aria-invalid={nameError ? 'true' : 'false'}
              aria-describedby={nameError ? 'adhoc-name-error' : undefined}
              style={nameEntryMode === 'new' ? undefined : { display: 'none' }}
            />
            {nameEntryMode === 'new' && nameError ? (
              <FieldError
                id='adhoc-name-error'
                data-testid='adhoc-name-error'
                role='alert'
              >
                {nameError}
              </FieldError>
            ) : null}
          </Field>
        )}

        {isFoodSafetyRecipeSuggestSection(section) ? (
          <FoodSafetyShoppingListSuggestBlock
            mode='logging'
            section={section}
            products={venueProducts}
            recipes={recipes}
            templates={templates}
            resampleKey={`${section}-${day?.localDate || ''}`}
            selectedTitle={activeTemplate ? '' : draft.definitionName}
            title='From your recipes'
            markOptional
            chipKind='recipe'
            recipesOnly
            onSelect={selectShoppingListProduct}
            testId='adhoc-recipe-suggest'
          />
        ) : null}

        {remembered.length > 0 ? (
          <TemplateRow data-testid='adhoc-remembered'>
            <TemplateLabel>
              {section === FOOD_SAFETY_SECTION.HOT_HOLDING
                ? 'Last foods'
                : 'Last time'}{' '}
              <OptionalMark>optional</OptionalMark>
            </TemplateLabel>
            <ChipRow>
              {remembered.map(memory => {
                const active =
                  !activeTemplate &&
                  String(draft.definitionName || '')
                    .trim()
                    .toLowerCase() ===
                    String(memory.name || '')
                      .trim()
                      .toLowerCase()
                return (
                  <Chip
                    key={memory.name}
                    type='button'
                    data-active={active ? 'true' : 'false'}
                    data-testid={`adhoc-remembered-${memory.name}`}
                    onClick={() => selectRemembered(memory)}
                  >
                    {memory.name}
                  </Chip>
                )
              })}
            </ChipRow>
          </TemplateRow>
        ) : null}

        {section !== FOOD_SAFETY_SECTION.DELIVERIES &&
        nameEntryMode === 'saved' &&
        templates.length > 0 ? (
          <TemplateRow data-testid='adhoc-shortcuts'>
            <TemplateLabel>{FOOD_SAFETY_SAVED_FOOD_HEADING}</TemplateLabel>
            <ChipRow>
              {visibleTemplates.map(def => {
                const kind = cookingKindLabel(def.config?.cookingKind)
                const active = String(activeTemplate?._id) === String(def._id)
                const needed = stillNeededIds.has(String(def._id))
                return (
                  <Chip
                    key={String(def._id)}
                    type='button'
                    data-active={active ? 'true' : 'false'}
                    data-needed={needed ? 'true' : 'false'}
                    onClick={() =>
                      active ? clearTemplate() : selectTemplate(def)
                    }
                  >
                    {def.name}
                    {kind ? ` · ${kind}` : ''}
                  </Chip>
                )
              })}
            </ChipRow>
            {quickPicksTruncated || quickPicksExpanded ? (
              <ShowMoreButton
                type='button'
                onClick={() => setQuickPicksExpanded(v => !v)}
                data-testid='adhoc-quick-picks-toggle'
              >
                {quickPicksExpanded
                  ? 'Show fewer'
                  : `Show ${hiddenQuickPickCount} more`}
              </ShowMoreButton>
            ) : null}
          </TemplateRow>
        ) : section !== FOOD_SAFETY_SECTION.DELIVERIES &&
          nameEntryMode === 'saved' &&
          canManage ? (
          <TemplateRow data-testid='adhoc-save-foods'>
            <SaveFoodsLine>{FOOD_SAFETY_SAVE_FOODS_LINE}</SaveFoodsLine>
            <SetupLink
              to={`/v/${venueId}/food-safety/setup?section=${section}`}
              data-testid='adhoc-save-foods-setup'
            >
              {FOOD_SAFETY_SAVE_FOODS_LINK}
            </SetupLink>
          </TemplateRow>
        ) : null}

        {section === FOOD_SAFETY_SECTION.DELIVERIES ? (
          <>
            <Field>
              <FieldLabel id='adhoc-bands-label'>What arrived</FieldLabel>
              <ChipRow role='group' aria-labelledby='adhoc-bands-label'>
                {Object.values(FOOD_SAFETY_DELIVERY_TEMP_BAND).map(band => {
                  const active = (draft.deliveryBands || []).some(
                    row => row.band === band,
                  )
                  return (
                    <Chip
                      key={band}
                      type='button'
                      data-active={active ? 'true' : 'false'}
                      data-needed='false'
                      aria-pressed={active}
                      onClick={() =>
                        patchDraft({
                          deliveryBands: toggleDeliveryBand(
                            draft.deliveryBands,
                            band,
                          ),
                        })
                      }
                      data-testid={`adhoc-band-${band}`}
                    >
                      {FOOD_SAFETY_DELIVERY_TEMP_PRESETS[band].label}
                    </Chip>
                  )
                })}
              </ChipRow>
              {(draft.deliveryBands || [])
                .filter(
                  row => row.band !== FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT,
                )
                .map(row => (
                  <TempInputRow key={row.band}>
                    <FieldLabel
                      as='label'
                      htmlFor={`adhoc-band-temp-${row.band}`}
                    >
                      {FOOD_SAFETY_DELIVERY_TEMP_PRESETS[row.band].label} temp
                    </FieldLabel>
                    <InputNumber
                      id={`adhoc-band-temp-${row.band}`}
                      value={deliveryBandTemp(draft.deliveryBands, row.band)}
                      onChange={value =>
                        patchDraft({
                          deliveryBands: setDeliveryBandTemp(
                            draft.deliveryBands,
                            row.band,
                            value,
                          ),
                        })
                      }
                      placeholder='°C'
                      step={0.1}
                      style={{ width: '7rem' }}
                      data-testid={`adhoc-band-temp-${row.band}`}
                    />
                    <RangeHint>
                      {`≤${
                        FOOD_SAFETY_DELIVERY_TEMP_PRESETS[row.band].tempMax
                      }°C`}
                    </RangeHint>
                  </TempInputRow>
                ))}
            </Field>
            <Field>
              <FieldLabel id='adhoc-decision-label'>
                Checked product decision
              </FieldLabel>
              <DecisionRow role='group' aria-labelledby='adhoc-decision-label'>
                <DecisionChip
                  type='button'
                  data-active={draft.accepted !== false ? 'true' : 'false'}
                  data-tone='accept'
                  onClick={() =>
                    patchDraft({
                      accepted: true,
                      rejectionCode: '',
                      rejectionScope: '',
                    })
                  }
                  aria-pressed={draft.accepted !== false}
                  data-testid='adhoc-accepted-yes'
                >
                  {FOOD_SAFETY_DELIVERY_ACCEPT_LABEL}
                </DecisionChip>
                <DecisionChip
                  type='button'
                  data-active={draft.accepted === false ? 'true' : 'false'}
                  data-tone='reject'
                  onClick={() => patchDraft({ accepted: false })}
                  aria-pressed={draft.accepted === false}
                  data-testid='adhoc-accepted-no'
                >
                  {FOOD_SAFETY_DELIVERY_REJECT_LABEL}
                </DecisionChip>
              </DecisionRow>
              {draft.accepted === false ? (
                <>
                  <RejectHint data-testid='adhoc-reject-hint'>
                    {FOOD_SAFETY_DELIVERY_REJECT_HINT}
                  </RejectHint>
                  <ChipRow
                    role='group'
                    aria-label='Why it went back'
                    data-testid='adhoc-reject-reasons'
                  >
                    {FOOD_SAFETY_DELIVERY_REJECTION_OPTIONS.map(option => (
                      <Chip
                        key={option.code}
                        type='button'
                        data-active={
                          draft.rejectionCode === option.code ? 'true' : 'false'
                        }
                        data-needed='false'
                        aria-pressed={draft.rejectionCode === option.code}
                        onClick={() =>
                          patchDraft({
                            rejectionCode: option.code,
                            rejectionScope: defaultDeliveryRejectionScope(
                              option.code,
                            ),
                          })
                        }
                        data-testid={`adhoc-reject-${option.code}`}
                      >
                        {option.label}
                      </Chip>
                    ))}
                  </ChipRow>
                  {draft.rejectionCode ? (
                    <ChipRow role='group' aria-label='What went back'>
                      {Object.entries(
                        FOOD_SAFETY_DELIVERY_REJECTION_SCOPE_LABELS,
                      ).map(([scope, label]) => (
                        <Chip
                          key={scope}
                          type='button'
                          data-active={
                            (draft.rejectionScope ||
                              defaultDeliveryRejectionScope(
                                draft.rejectionCode,
                              )) === scope
                              ? 'true'
                              : 'false'
                          }
                          data-needed='false'
                          aria-pressed={draft.rejectionScope === scope}
                          onClick={() => patchDraft({ rejectionScope: scope })}
                          data-testid={`adhoc-reject-scope-${scope}`}
                        >
                          {label}
                        </Chip>
                      ))}
                    </ChipRow>
                  ) : null}
                  <Input
                    value={draft.action?.notes || ''}
                    onChange={e => patchAction({ notes: e.target.value })}
                    placeholder='Short note (optional)'
                    data-testid='adhoc-reject-note'
                  />
                </>
              ) : null}
            </Field>
          </>
        ) : null}

        {isCooling ? (
          <CoolingTimes data-testid='adhoc-cooling-times'>
            <Field>
              <FieldLabel as='label' htmlFor='adhoc-cooling-start-field'>
                Went in to cool
              </FieldLabel>
              <CoolingRow>
                <FoodSafetyClockField
                  id='adhoc-cooling-start-field'
                  value={draft.startTime || ''}
                  onChange={startTime => patchDraft({ startTime })}
                  hasError={Boolean(coolingError)}
                  aria-label='Went in to cool'
                  data-testid='adhoc-cooling-start'
                />
                <ChipRow>
                  {FOOD_SAFETY_COOLING_START_CHIPS.map(chip => (
                    <Chip
                      key={chip.minutes}
                      type='button'
                      data-active='false'
                      data-needed='false'
                      onClick={() =>
                        patchDraft({
                          startTime: coolingStartTimeBefore({
                            localDate: day?.localDate,
                            finishTime: draft.finishTime,
                            minutes: chip.minutes,
                          }),
                        })
                      }
                      data-testid={`adhoc-cooling-start-${chip.minutes}`}
                    >
                      {chip.label}
                    </Chip>
                  ))}
                </ChipRow>
              </CoolingRow>
            </Field>
            <CoolingRow>
              <Field>
                <FieldLabel as='label' htmlFor='adhoc-cooling-start-temp-field'>
                  Start temp (optional)
                </FieldLabel>
                <InputNumber
                  id='adhoc-cooling-start-temp-field'
                  value={draft.startTemp}
                  onChange={startTemp => patchDraft({ startTemp })}
                  placeholder='°C'
                  step={0.1}
                  style={{ width: '7rem' }}
                  data-testid='adhoc-cooling-start-temp'
                />
              </Field>
              {coolingStartMode ? null : (
                <Field>
                  <FieldLabel as='label' htmlFor='adhoc-cooling-finish-field'>
                    Finished
                  </FieldLabel>
                  <FoodSafetyClockField
                    id='adhoc-cooling-finish-field'
                    value={draft.finishTime || ''}
                    onChange={finishTime => patchDraft({ finishTime })}
                    hasError={Boolean(coolingError)}
                    aria-label='Finished'
                    data-testid='adhoc-cooling-finish'
                  />
                </Field>
              )}
            </CoolingRow>
            {coolingError ? (
              <FieldError role='alert' data-testid='adhoc-cooling-error'>
                {coolingError}
              </FieldError>
            ) : coolingStartMode ? (
              <CoolingDuration
                data-tone='unknown'
                data-testid='adhoc-cooling-remind'
              >
                Check after{' '}
                {formatCoolingDuration(
                  resolveCoolingRemindAfterMinutes(day?.coolingTarget),
                )}
              </CoolingDuration>
            ) : (
              <CoolingDuration
                data-tone={coolingTooLong ? 'fail' : 'unknown'}
                data-testid='adhoc-cooling-duration'
              >
                {formatCoolingDurationLine(
                  coolingWindow?.error ? null : coolingWindow?.minutes,
                  coolingMaxMinutes,
                )}
              </CoolingDuration>
            )}
            {viewingToday ? (
              <Button
                type='link'
                onClick={() =>
                  setCoolingComposerMode(coolingStartMode ? 'oneshot' : 'start')
                }
                data-testid='adhoc-cooling-mode-toggle'
              >
                {coolingStartMode
                  ? 'Log a finished cooling instead'
                  : 'Start cooling instead'}
              </Button>
            ) : null}
          </CoolingTimes>
        ) : null}

        {submitError ? (
          <FoodSafetySubmitErrorBox
            data-testid='adhoc-submit-error'
            role='status'
          >
            <FoodSafetySubmitErrorLead>
              {FOOD_SAFETY_SUBMIT_ERROR_LEAD}
            </FoodSafetySubmitErrorLead>
            {submitError}
          </FoodSafetySubmitErrorBox>
        ) : null}

        <EntryRow data-testid='adhoc-entry-row'>
          {section === FOOD_SAFETY_SECTION.DELIVERIES ||
          coolingStartMode ? null : (
            <TempField data-tone={draftTempTone}>
              <TempHeading>
                <FieldLabel as='label' htmlFor='adhoc-temp-field'>
                  {meta.tempLabel}
                </FieldLabel>
                {meta.tempHelper ? (
                  <TempHelper data-testid='adhoc-temp-helper'>
                    {meta.tempHelper}
                  </TempHelper>
                ) : null}
              </TempHeading>
              <TempInputRow>
                <InputNumber
                  id='adhoc-temp-field'
                  data-testid='adhoc-temp-input'
                  value={tempValue}
                  onChange={setTempValue}
                  onPressEnter={submitFromTemp}
                  placeholder='°C'
                  step={0.1}
                  style={{ width: '7rem' }}
                />
                <span data-temp-unit='true'>°C</span>
                {rangeHint ? (
                  <RangeHint
                    data-tone={draftTempTone}
                    data-testid='adhoc-range-hint'
                  >
                    {rangeHint}
                  </RangeHint>
                ) : null}
              </TempInputRow>
            </TempField>
          )}
          <Button
            type={submitAffordance.quiet ? 'default' : 'primary'}
            loading={submitting}
            onClick={() => {
              if (!coolingStartMode && submitAffordance.needsChoice) {
                patchDraft({ actionNudged: true })
                focusFirstActionChip()
                return
              }
              submit()
            }}
            data-testid='adhoc-submit'
            data-chrome={submitAffordance.quiet ? 'quiet' : undefined}
          >
            {coolingStartMode ? 'Start cooling' : submitAffordance.label}
          </Button>
          {canMarkNotRequired ? (
            <Button
              loading={submitting}
              onClick={() => skipQuickPickToday(activeTemplate)}
              data-testid='adhoc-skip-today'
              data-chrome='quiet'
            >
              Not Required Today
            </Button>
          ) : null}
        </EntryRow>
        {canMarkNotRequired ? (
          <NotRequiredNote
            value={notRequiredNote}
            onChange={event => setNotRequiredNote(event.target.value)}
            placeholder='Note, optional'
            aria-label='Note, optional'
            data-testid='adhoc-not-required-note'
          />
        ) : null}

        {needsAction ? (
          <FoodSafetyCorrectiveActionPanel
            section={section}
            variant={rejected ? 'rejected' : 'action'}
            headline={
              coolingTooLong && !outOfRange
                ? formatCoolingTimeHeadline(
                    coolingWindow.minutes,
                    coolingMaxMinutes,
                  )
                : heldTooLongText && !outOfRange
                ? formatFoodSafetyHeldTimeHeadline(
                    heldSince.minutes,
                    holdingMaxMinutes,
                  )
                : null
            }
            reading={{
              value: tempValue,
              tempMin: config.tempMin,
              tempMax: config.tempMax,
            }}
            draft={draft.action}
            onChange={patchAction}
            showRecheck={recheckOffered}
            error={actionError}
            focusOnError
            onNoteEnter={submit}
            onSaveWithoutAction={submit}
            withoutActionLabel={rejected ? 'Save without reason' : undefined}
            testIdPrefix='adhoc-corrective'
          />
        ) : null}

        {heldSinceOffered ? (
          <CoolingTimes data-testid='adhoc-held-since'>
            <Field>
              <FieldLabel as='label' htmlFor='adhoc-held-since-field'>
                Hot holding started at (optional)
              </FieldLabel>
              <CoolingRow>
                <FoodSafetyClockField
                  id='adhoc-held-since-field'
                  value={draft.heldSinceTime || ''}
                  onChange={heldSinceTime => patchDraft({ heldSinceTime })}
                  aria-label='Hot holding started at (optional)'
                  data-testid='adhoc-held-since-time'
                />
                <ChipRow>
                  {FOOD_SAFETY_HELD_SINCE_CHIPS.map(chip => (
                    <Chip
                      key={chip.minutes}
                      type='button'
                      data-active='false'
                      data-needed='false'
                      onClick={() =>
                        patchDraft(foodSafetyHeldSinceBefore(chip.minutes))
                      }
                      data-testid={`adhoc-held-since-${chip.minutes}`}
                    >
                      {chip.label}
                    </Chip>
                  ))}
                </ChipRow>
              </CoolingRow>
              {heldSince?.startOptions ? (
                <ChipRow data-testid='adhoc-held-since-options'>
                  {heldSince.startOptions.map(option => (
                    <Chip
                      key={option.heldSinceAt}
                      type='button'
                      data-active='false'
                      data-needed='false'
                      onClick={() =>
                        patchDraft({ heldSinceAt: option.heldSinceAt })
                      }
                    >
                      {option.label}
                    </Chip>
                  ))}
                </ChipRow>
              ) : null}
              <RangeHint>
                Enter this device’s local time. A later clock time is treated as
                yesterday. Leave blank if the start time is unknown.
              </RangeHint>
            </Field>
            {heldError || heldSince?.error ? (
              <FieldError role='alert' data-testid='adhoc-held-since-error'>
                {heldError || heldSince.error}
              </FieldError>
            ) : (
              <CoolingDuration
                data-tone={heldTooLongText ? 'fail' : 'unknown'}
                data-testid='adhoc-held-since-line'
              >
                {formatFoodSafetyHeldSinceLine(
                  heldSince?.minutes,
                  holdingMaxMinutes,
                )}
              </CoolingDuration>
            )}
          </CoolingTimes>
        ) : null}

        {isFoodSafetyShoppingListSuggestSection(section) ? (
          <FoodSafetyShoppingListSuggestBlock
            mode='logging'
            section={section}
            products={venueProducts}
            recipes={recipes}
            templates={templates}
            resampleKey={`${section}-${day?.localDate || ''}`}
            selectedTitle={activeTemplate ? '' : draft.definitionName}
            title='From your list'
            markOptional
            hint={
              section === FOOD_SAFETY_SECTION.DELIVERIES
                ? FOOD_SAFETY_DELIVERY_LIST_PRODUCT_HINT
                : undefined
            }
            chipKind='list'
            yieldToRecipes={isFoodSafetyRecipeSuggestSection(section)}
            supplierName=''
            limit={
              section === FOOD_SAFETY_SECTION.DELIVERIES
                ? deliveryFrequentBudget.listLimit
                : undefined
            }
            onSelect={selectShoppingListProduct}
            testId='adhoc-list-suggest'
          />
        ) : null}
      </Composer>

      {activeBatches.length ? (
        <ActiveBatchList data-testid='adhoc-cooling-active'>
          {activeBatches.map(batch => {
            const batchId = String(batch._id)
            const open = finishingId === batchId
            const elapsed = formatCoolingElapsedVsTarget({
              startAt: batch.payload?.startAt,
              remindAfterMinutes:
                batch.payload?.remindAfterMinutes ||
                batch.remindAfterMinutes ||
                resolveCoolingRemindAfterMinutes(day?.coolingTarget),
            })
            const name =
              batch.productName ||
              batch.definitionName ||
              batch.payload?.productName ||
              'Cooling'
            return (
              <LogCard
                key={batchId}
                data-testid={`adhoc-cooling-batch-${batchId}`}
              >
                <LogTitle>{name}</LogTitle>
                <LogDetail>{elapsed}</LogDetail>
                {open ? (
                  <CoolingRow>
                    <Field>
                      <FieldLabel>Finish temp</FieldLabel>
                      <InputNumber
                        value={finishDraft.finalTemp}
                        onChange={finalTemp =>
                          setFinishDraft(prev => ({ ...prev, finalTemp }))
                        }
                        placeholder='°C'
                        step={0.1}
                        style={{ width: '7rem' }}
                        data-testid='adhoc-cooling-finish-temp'
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Finished</FieldLabel>
                      <FoodSafetyClockField
                        value={finishDraft.finishTime || ''}
                        onChange={finishTime =>
                          setFinishDraft(prev => ({
                            ...prev,
                            finishTime,
                          }))
                        }
                        data-testid='adhoc-cooling-finish-time'
                      />
                    </Field>
                    <Button
                      type='primary'
                      loading={submitting}
                      onClick={() => submitFinishBatch(batch)}
                      data-testid='adhoc-cooling-finish-save'
                    >
                      Finish
                    </Button>
                  </CoolingRow>
                ) : (
                  <Button
                    onClick={() => {
                      setFinishingId(batchId)
                      setFinishDraft({ finalTemp: null, finishTime: '' })
                    }}
                    data-testid={`adhoc-cooling-finish-${batchId}`}
                  >
                    Finish
                  </Button>
                )}
              </LogCard>
            )
          })}
        </ActiveBatchList>
      ) : null}

      <LogsHeader>
        <H3>{logsHeader}</H3>
      </LogsHeader>
      {logs.length === 0 ? (
        <EmptyLogs data-testid='adhoc-empty-logs'>
          {formatFoodSafetyAdHocEmptyLogs({ viewingToday })}
        </EmptyLogs>
      ) : (
        <LogList>
          {logs.map(log => {
            const line = formatAdHocLogLine(section, log)
            const exception = pickFoodSafetyLogException(log)
            const canUndo = canUndoAdHocSkipLog(log, {
              viewingLocalDate: day?.localDate,
              todayLocalDate: day?.todayLocalDate,
            })
            return (
              <LogCard key={String(log._id)}>
                <LogTitle>{line.title}</LogTitle>
                {line.tempLabel || line.extrasLabel ? (
                  <LogDetail>
                    {line.tempLabel ? (
                      <LogTemp data-tone={line.tone}>{line.tempLabel}</LogTemp>
                    ) : null}
                    {line.statusLabel ? (
                      <LogStatus data-tone={line.tone}>
                        {line.tempLabel ? ' · ' : ''}
                        {line.statusLabel}
                      </LogStatus>
                    ) : null}
                    {line.extrasLabel ? (
                      <LogExtra>
                        {line.tempLabel || line.statusLabel ? ' · ' : ''}
                        {line.extrasLabel}
                      </LogExtra>
                    ) : null}
                  </LogDetail>
                ) : line.detail ? (
                  <LogDetail>{line.detail}</LogDetail>
                ) : null}
                <FoodSafetyExceptionStatus
                  exception={exception}
                  fallbackNote={line.corrective}
                  testId={`adhoc-needs-action-${log._id}`}
                />
                <LogMeta>{line.meta}</LogMeta>
                <FoodSafetyCorrectReading
                  log={log}
                  testId={`adhoc-correct-${log._id}`}
                  disabled={submitting}
                  onCorrect={fields => saveCorrection(log, fields)}
                />
                <FoodSafetyExceptionFollowUp
                  exception={exception}
                  section={section}
                  fallbackReading={{
                    value: log?.payload?.value ?? log?.payload?.finalTemp,
                  }}
                  viewingToday={viewingToday}
                  followUps={followUps}
                  testIdPrefix='adhoc'
                  rowKey={String(log._id)}
                />
                {canUndo ? (
                  <UndoSkipButton
                    type='link'
                    size='small'
                    disabled={submitting}
                    onClick={() => confirmUndoSkipLog(log)}
                    data-testid={`adhoc-undo-skip-${log._id}`}
                  >
                    Undo
                  </UndoSkipButton>
                ) : null}
                {canManage &&
                canSaveFoodSafetyLogAsTemplate(section, log, templates) ? (
                  <UndoSkipButton
                    type='link'
                    size='small'
                    disabled={
                      submitting || savingTemplateId === String(log._id)
                    }
                    onClick={() => saveLogAsTemplate(log)}
                    data-testid={`adhoc-save-template-${log._id}`}
                  >
                    {FOOD_SAFETY_SAVE_AS_TEMPLATE}
                  </UndoSkipButton>
                ) : null}
              </LogCard>
            )
          })}
        </LogList>
      )}

      <FoodSafetyReportsPaywallModal
        visible={historyPaywallOpen}
        reason={FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER}
        olderRecordCount={(day?.historyAccess || lockAccess)?.olderRecordCount}
        upgradeHint={(day?.historyAccess || lockAccess)?.upgradeHint}
        onClose={() => setHistoryPaywallOpen(false)}
      />
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  ${ollieFabPageClearanceCss}

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
    ${ollieFabPageClearanceCss}
  `};
`

const TopBar = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  font-weight: 600;
`

const DeliverySources = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.75rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const StandingCard = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1 1 16rem;
  margin-bottom: 0;
`

const StandingTitle = styled(H3)`
  margin: 0;
  font-size: 1rem;
`

const Composer = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const ComposerTitle = styled(H3)`
  margin: 0;
  font-size: 1rem;
`

const SaveFoodsLine = styled.span`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const SetupLink = styled(Link)`
  align-self: flex-start;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const TemplateRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const TemplateLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const OptionalMark = styled.span`
  margin-left: 0.4rem;
  font-weight: 500;
  letter-spacing: 0;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const ShowMoreButton = styled.button`
  align-self: flex-start;
  margin: 0.15rem 0 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colours.bbqBlack || '#132d07'};
  }
`

const Chip = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  padding: 0.25rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  max-width: 100%;
  text-align: left;
  overflow-wrap: anywhere;

  &[data-kind='list'] {
    border-style: dashed;
    color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
  }

  &[data-active='true'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    border-style: solid;
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    color: inherit;
  }

  &[data-needed='true']:not([data-active='true']) {
    border-color: #c47a12;
    color: #8a5a0a;
  }
`

/** 29.34 — stays visible while scrolling long Cooking / Cooling / Deliveries lists. */
const StillNeededSticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: 0.55rem 0.75rem;
  border: 1px solid #e8c48a;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  border-left: 3px solid #c47a12;
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
`

const StillNeededStickyLabel = styled.strong`
  font-size: 0.9rem;
  color: #8a5a0a;
`

const StillNeededStickyHint = styled.span`
  display: block;
  width: 100%;
  font-size: 0.8rem;
  color: #8a5a0a;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  &[data-invalid='true'] .ant-input {
    border-color: #c45c26;
  }

  &[data-invalid='true'] .ant-input:focus {
    border-color: #c45c26;
    box-shadow: 0 0 0 2px rgba(196, 92, 38, 0.15);
  }
`

const FieldLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const FieldError = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #c45c26;
`

const EntryRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

const NotRequiredNote = styled(Input)`
  display: block;
  width: 100%;
  margin-top: 0.5rem;
`

const TempField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;

  &[data-tone='fail'] .ant-input-number {
    color: #c47a12;
  }

  &[data-tone='pass'] .ant-input-number {
    color: #5a9e0b;
  }

  &[data-tone='fail'] [data-temp-unit='true'] {
    color: #c47a12;
  }

  &[data-tone='pass'] [data-temp-unit='true'] {
    color: #5a9e0b;
  }
`

const TempHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`

const TempHelper = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
  line-height: 1.25;
`

const TempInputRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
`

const RangeHint = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};

  &[data-tone='fail'] {
    color: #c47a12;
    font-weight: 600;
  }

  &[data-tone='pass'] {
    color: #5a9e0b;
  }
`

const CoolingTimes = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const CoolingRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
`

const CoolingDuration = styled(RangeHint)`
  display: block;
`

const DecisionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`

const DecisionChip = styled.button`
  flex: 1;
  min-height: 2.75rem;
  padding: 0.55rem 0.75rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border: 1.5px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colours.bbqBlack || '#1a1a1a'};
  cursor: pointer;

  &[data-active='true'][data-tone='accept'] {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    font-weight: 700;
  }

  &[data-active='true'][data-tone='reject'] {
    background: #fff2e8;
    border-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    font-weight: 700;
  }
`

const RejectHint = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  line-height: 1.35;
`

const ActiveBatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const LogsHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const EmptyLogs = styled(BodyText)`
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const LogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const LogCard = styled(FoodSafetySurfaceCard)`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
`

const LogTitle = styled.div`
  font-weight: 600;
`

const LogDetail = styled.div`
  font-size: 0.95rem;
`

const LogTemp = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};

  &[data-tone='pass'] {
    color: #5a9e0b;
  }

  &[data-tone='fail'] {
    color: #c47a12;
  }
`

const LogStatus = styled.span`
  font-weight: 600;
  color: #c47a12;
`

const LogExtra = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const LogMeta = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const UndoSkipButton = styled(Button)`
  margin-top: 0.35rem;
  padding-left: 0;
  height: auto;
`

export default FoodSafetyAdHocSectionScreen
